import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Calendar, Coins, Zap } from "lucide-react";

const Abonnemang = () => {
  const { language, setLanguage, content } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <Header 
        language={language} 
        onLanguageChange={setLanguage} 
        content={content.header} 
      />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-subtle">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent" style={{lineHeight: '1.4em'}}>
              Gör ni mycket film? Då kan ni få fördelaktiga priser.
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Vi erbjuder månadsabonnemang på film till fördelaktiga priser för företag som behöver kontinuerlig videoproduktion.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-slate-900/5 dark:bg-slate-900/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Fördelar med våra månadsabonnemang</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Perfekt för företag som vill skala sin videoproduktion och få bättre värde för pengarna.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card className="text-center">
                <CardHeader>
                  <div className="w-16 h-16 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4">
                    <Coins className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">Fördelaktiga priser</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Betydligt lägre pris per video jämfört med engångsprojekt.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="w-16 h-16 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">Förutsägbarhet</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Fast månadsbudget och garanterad kapacitet för era videobehov.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="w-16 h-16 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">Prioriterad service</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Abonnemangskunder får prioriterad hantering och snabbare leveranser.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Subscription Packages */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Våra abonnemang</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Välj det abonnemang som passar ert företags videobehov bäst.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Basic Package */}
              <Card className="relative">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold">Basic</CardTitle>
                  <CardDescription>För mindre företag</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">60s</span>
                    <p className="text-muted-foreground">färdig film /månad</p>
                    <p className="text-sm text-muted-foreground mt-1">uppdelat på max 4 filmer</p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>60 sekunder färdig film per månad</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Uppdelat på max 4 separata filmer</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Alla grundfunktioner inkluderade</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>2 revisionsrundor per film</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>E-postsupport</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Professional Package */}
              <Card className="relative border-primary/50 shadow-lg">
                <div className="absolute top-0 left-0 right-0 bg-gradient-primary text-white text-center py-2">
                  <span className="text-sm font-medium">MEST POPULÄR</span>
                </div>
                <CardHeader className="text-center pt-12">
                  <CardTitle className="text-2xl font-bold">Professional</CardTitle>
                  <CardDescription>För växande företag</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">180s</span>
                    <p className="text-muted-foreground">färdig film /månad</p>
                    <p className="text-sm text-muted-foreground mt-1">uppdelat på max 8 filmer</p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>180 sekunder färdig film per månad</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Uppdelat på max 8 separata filmer</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Alla grundfunktioner inkluderade</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>2 revisionsrundor per film</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Prioriterad support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Månadsvis strategisamtal</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Custom Package */}
              <Card className="relative">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold">Custom</CardTitle>
                  <CardDescription>För stora organisationer</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">Anpassat</span>
                    <p className="text-muted-foreground">efter era behov</p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Skräddarsydda lösningar</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Anpassad mängd film per månad</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Alla funktioner inkluderade</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>3 revisionsrundor per film</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Dedikerad projektledare</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Prioriterad support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Specialanpassade tjänster</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-24 bg-gradient-subtle">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Kontakta oss för er skräddarsydda lösning
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Vi skräddarsyr våra abonnemang efter era specifika behov. Kontakta oss så diskuterar vi vad som passar er bäst.
              </p>
              <Button 
                asChild 
                size="lg" 
                className="bg-gradient-primary border-0 hover:opacity-90 text-lg px-8 py-6"
              >
                <a href="mailto:info@volymkommunikation.se">
                  info@volymkommunikation.se
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer content={content.footer} />
    </div>
  );
};

export default Abonnemang;